import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  forkJoin,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { BaseCrudService } from './base-crud.service';
import { CreateQuoteRequestDto } from '../dtos/create-quote-request.dto';
import { UpdateQuoteRequestDto } from '../dtos/update-quote-request.dto';
import { QuoteResponseDto } from '../dtos/quote-response.dto';
import { QuotePaymentRequestDto } from '../dtos/quote-payment-request.dto';
import { QuotePaymentResponseDto } from '../dtos/quote-payment-response.dto';
import { LocalStoreService } from '../../../common/services/storage.service';
import { IssueQuoteRequestDto } from '../dtos/issue-quote-request.dto';
import { IssueQuoteResponseDto } from '../dtos/issue-quote-response.dto';
import { InsuranceTypesService } from '../../../features/insurance-types/services/insurance-types.service';
import { PaymentSessionRequestDto } from '../dtos/payment-session-request.dto';
import { PaymentSessionResponseDto } from '../dtos/payment-session-response.dto';
import { Gender } from '../enums/gender.enum';
import { AuthService } from '../../../features/auth/services/auth.service';
import { toIsoDate } from '../../../common/utils/date-utils';
import { QuoteAddressRequestDto } from '../dtos/quote-address-request.dto';
import { normalizeProducerCode, denormalizeProducerCode } from '../../../common/utils/quote-detail-mappers';

/**
 * Quotes API service aligned with swagger paths
 * - POST /api/Quotes
 * - PUT  /api/Quotes/{quoteId}
 * - GET  /api/Quotes/{quoteId}?email=...
 * - POST /api/Quotes/{quoteId}/payment
 * - POST /api/Quotes/{quoteId}/issue
 */
@Injectable({ providedIn: 'root' })
export class QuotesService extends BaseCrudService<QuoteResponseDto> {
  private static readonly CURRENT_QUOTE_KEY = 'currentQuote';
  controller = 'Quotes';
  private readonly store = inject(LocalStoreService);
  private readonly insuranceTypesService = inject(InsuranceTypesService);
  private readonly authService = inject(AuthService);
  private currentQuoteSource = new BehaviorSubject<QuoteResponseDto | null>(
    null,
  );
  currentQuote$ = this.currentQuoteSource.asObservable();

  constructor() {
    super();
    // Initialize current quote from session storage on service creation
    const existing = this.store.getSessionItem<QuoteResponseDto>(
      QuotesService.CURRENT_QUOTE_KEY,
    );
    if (existing) {
      // Immediately set the existing quote so UI can render selections,
      // then enrich asynchronously with full InsuranceType data.
      this.setCurrentQuote(existing);
      // Ensure insurance types are present even when loading from session
      // Enrich asynchronously and update session copy too
      this.enrichWithInsuranceTypes(existing).subscribe({
        next: (enriched) => this.setCurrentQuote(enriched),
        error: () => this.setCurrentQuote(existing),
      });
    }
  }

  get currentQuote(): QuoteResponseDto | null {
    return this.currentQuoteSource.value;
  }

  setCurrentQuote(q: QuoteResponseDto | null) {
    this.currentQuoteSource.next(q);
    if (q) {
      this.store.setSessionItem(QuotesService.CURRENT_QUOTE_KEY, q);
    } else {
      this.store.removeSessionItem(QuotesService.CURRENT_QUOTE_KEY);
    }
  }

  /** Update the in-memory (and session) copy of the current quote without calling the API */
  setCurrentQuoteLocal(q: QuoteResponseDto | null): void {
    this.setCurrentQuote(q);
  }

  clearCurrentQuote() {
    this.setCurrentQuote(null);
  }

  /**
   * Backend requires Street + TownCode when Address object is present.
   * Send null address when those required fields are missing.
   */
  private normalizeAddressForRequest(
    address: QuoteAddressRequestDto | null | undefined,
  ): QuoteAddressRequestDto | null {
    if (!address) return null;
    const street =
      typeof address.street === 'string' ? address.street.trim() : '';
    if (!street || !address.townCode) return null;
    return {
      ...address,
      street,
    };
  }

  /** Fill retailer/supervisor/manager/branch from the logged-in bank user when empty */
  private applyUserAttribution<
    T extends {
      retailer?: number | null;
      supervisor?: number | null;
      manager?: number | null;
      branch?: number | null;
    },
  >(body: T): T {
    const userId = this.authService.userId;
    if (!userId) return body;

    const attributed = { ...body };
    if (this.authService.isRetailer && !attributed.retailer) {
      attributed.retailer = userId;
    }
    if (this.authService.isSupervisor && !attributed.supervisor) {
      attributed.supervisor = userId;
    }
    if (this.authService.isManager && !attributed.manager) {
      attributed.manager = userId;
    }
    if (!attributed.branch && this.authService.branchId) {
      attributed.branch = this.authService.branchId;
    }
    return attributed;
  }

  /** Create a new Quote */
  createQuote(body: CreateQuoteRequestDto): Observable<QuoteResponseDto> {
    return this.create<CreateQuoteRequestDto, QuoteResponseDto>(
      this.applyUserAttribution(body),
    ).pipe(
      switchMap((resp) => this.enrichWithInsuranceTypes(resp)),
      tap((resp) => this.setCurrentQuote(resp)),
    );
  }

  /** Update an existing Quote */
  updateQuote(
    quoteId: number,
    body: UpdateQuoteRequestDto,
  ): Observable<QuoteResponseDto> {
    // Capture documentExpiresAt per detail/insured role before the API call — the backend
    // may not return this field, so we need to re-inject it after the response.
    // We capture from BOTH the current local quote AND the request body (the body
    // contains the freshly entered form value which may not be in the local quote yet).
    const cur = this.currentQuote;

    const normalizeRelation = (rel: any): 'spouse' | 'child' | 'other' | null => {
      if (rel == null) return null;
      const r = String(rel).toLowerCase();
      if (['conyuge', 'cónyuge', 'conyugue', 'spouse'].includes(r)) {
        return 'spouse';
      }
      if (['child', 'hijo', 'hija', 'hijx', 'niño', 'niña'].includes(r)) {
        return 'child';
      }
      if (['madre', 'padre', 'hermano', 'hermana', 'abuelo', 'abuela',
        'nieto', 'nieta', 'primo', 'prima', 'tio', 'tía', 'tia',
        'sobrino', 'sobrina', 'other'].includes(r)) {
        return 'other';
      }
      return null;
    };

    const docExpByDetail = new Map<
      number,
      {
        byPersonId: Map<number, string>;
        primary?: string;
        spouse?: string;
        children: string[];
        others: string[];
      }
    >();
    const ensureDocExpBucket = (detailId: number) => {
      let bucket = docExpByDetail.get(detailId);
      if (!bucket) {
        bucket = { byPersonId: new Map<number, string>(), children: [], others: [] };
        docExpByDetail.set(detailId, bucket);
      }
      return bucket;
    };
    const captureDocExpFromDetail = (detail: any): void => {
      if (detail?.id == null) return;
      const bucket = ensureDocExpBucket(detail.id);
      const childQueue: string[] = [];
      (detail?.insuredPeople || []).forEach((ip: any) => {
        const exp = ip?.person?.documentExpiresAt;
        if (!exp) return;

        const pid = ip?.personId ?? ip?.person?.id;
        if (typeof pid === 'number') {
          bucket!.byPersonId.set(pid, exp);
        }

        if (ip?.isPrimary) {
          bucket!.primary = exp;
          return;
        }

        const rel = normalizeRelation(
          ip?.person?.primaryInsuredRelation ?? ip?.person?.relationship,
        );
        if (rel === 'spouse') {
          bucket!.spouse = exp;
          return;
        }
        if (rel === 'child') {
          childQueue.push(exp);
        }
        if (rel === 'other') {
          bucket!.others.push(exp);
        }
      });
      if (childQueue.length) {
        bucket.children = childQueue;
      }
    };
    (cur?.details || []).forEach((d) => captureDocExpFromDetail(d));
    (((body as any)?.details as any[]) || []).forEach((d) =>
      captureDocExpFromDetail(d),
    );

    // Capture birthDate per detail/insured role before API call — backend may omit it.

    const birthByDetail = new Map<
      number,
      {
        byPersonId: Map<number, string>;
        primary?: string;
        spouse?: string;
        children: string[];
        others: string[];
      }
    >();
    const ensureBirthBucket = (detailId: number) => {
      let bucket = birthByDetail.get(detailId);
      if (!bucket) {
        bucket = { byPersonId: new Map<number, string>(), children: [], others: [] };
        birthByDetail.set(detailId, bucket);
      }
      return bucket;
    };
    const captureBirthDatesFromDetail = (detail: any): void => {
      if (detail?.id == null) return;
      const bucket = ensureBirthBucket(detail.id);
      const childQueue: string[] = [];
      (detail?.insuredPeople || []).forEach((ip: any) => {
        const bd = ip?.person?.birthDate;
        if (!bd) return;

        const pid = ip?.personId ?? ip?.person?.id;
        if (typeof pid === 'number') {
          bucket!.byPersonId.set(pid, bd);
        }

        if (ip?.isPrimary) {
          bucket!.primary = bd;
          return;
        }

        const rel = normalizeRelation(
          ip?.person?.primaryInsuredRelation ?? ip?.person?.relationship,
        );
        if (rel === 'spouse') {
          bucket!.spouse = bd;
          return;
        }
        if (rel === 'child') {
          childQueue.push(bd);
        }
        if (rel === 'other') {
          bucket!.others.push(bd);
        }
      });
      if (childQueue.length) {
        bucket.children = childQueue;
      }
    };
    (cur?.details || []).forEach((d) => captureBirthDatesFromDetail(d));
    (((body as any)?.details as any[]) || []).forEach((d) =>
      captureBirthDatesFromDetail(d),
    );

    // Capture isPep per detail/insured role before API call — backend may coerce null to false.
    const isPepByDetail = new Map<
      number,
      {
        byPersonId: Map<number, boolean | null>;
        primary?: boolean | null;
        spouse?: boolean | null;
        children: Array<boolean | null>;
        others: Array<boolean | null>;
      }
    >();
    const ensureIsPepBucket = (detailId: number) => {
      let bucket = isPepByDetail.get(detailId);
      if (!bucket) {
        bucket = { byPersonId: new Map<number, boolean | null>(), children: [], others: [] };
        isPepByDetail.set(detailId, bucket);
      }
      return bucket;
    };
    const captureIsPepFromDetail = (detail: any): void => {
      if (detail?.id == null) return;
      const bucket = ensureIsPepBucket(detail.id);
      const childQueue: Array<boolean | null> = [];
      (detail?.insuredPeople || []).forEach((ip: any) => {
        const pep = ip?.person?.isPep;
        if (pep !== true && pep !== false && pep !== null) return;

        const pid = ip?.personId ?? ip?.person?.id;
        if (typeof pid === 'number') {
          bucket!.byPersonId.set(pid, pep);
        }

        if (ip?.isPrimary) {
          bucket!.primary = pep;
          return;
        }

        const rel = normalizeRelation(
          ip?.person?.primaryInsuredRelation ?? ip?.person?.relationship,
        );
        if (rel === 'spouse') {
          bucket!.spouse = pep;
          return;
        }
        if (rel === 'child') {
          childQueue.push(pep);
        }
        if (rel === 'other') {
          bucket!.others.push(pep);
        }
      });
      if (childQueue.length) {
        bucket.children = childQueue;
      }
    };
    (cur?.details || []).forEach((d) => captureIsPepFromDetail(d));
    (((body as any)?.details as any[]) || []).forEach((d) =>
      captureIsPepFromDetail(d),
    );

    // Preserve primary insured contact + address data per detail to avoid
    // losing required values when backend omits nested fields in PUT response.
    const primaryByDetail = new Map<
      number,
      {
        address?: any;
        contactEmail?: string | null;
        homePhone?: string | null;
        mobilePhone?: string | null;
      }
    >();
    const upsertPrimary = (detail: any): void => {
      if (detail?.id == null) return;
      const primary = (detail?.insuredPeople || []).find(
        (ip: any) => !!ip?.isPrimary,
      );
      if (!primary) return;
      const prev = primaryByDetail.get(detail.id) || {};
      const address = this.normalizeAddressForRequest(primary?.person?.address);
      const next = {
        address: address ?? prev.address,
        contactEmail: primary?.contactEmail ?? prev.contactEmail ?? null,
        homePhone: primary?.homePhone ?? prev.homePhone ?? null,
        mobilePhone: primary?.mobilePhone ?? prev.mobilePhone ?? null,
      };
      primaryByDetail.set(detail.id, next);
    };
    (cur?.details || []).forEach((d) => upsertPrimary(d));
    (((body as any)?.details as any[]) || []).forEach((d) => upsertPrimary(d));

    // BaseCrudService.update signature uses Partial<Y>; cast to any here.
    return this.update<QuoteResponseDto>(
      String(quoteId),
      this.applyUserAttribution(body) as any,
    ).pipe(
      switchMap((resp) => this.enrichWithInsuranceTypes(resp)),
      tap((enriched) => {
        // Re-inject documentExpiresAt if the API didn't return it
        if (docExpByDetail.size && enriched?.details) {
          enriched.details.forEach((d: any) => {
            if (d?.id == null) return;
            const bucket = docExpByDetail.get(d.id);
            if (!bucket) return;

            let childIdx = 0;
            (d.insuredPeople || []).forEach((ip: any) => {
              if (!ip?.person) return;
              if (ip.person.documentExpiresAt) return;
              // A cédula has no expiration date: never restore a previous passport date
              if (ip.person.documentType === 'IdCard') return;

              const pid = ip?.personId ?? ip?.person?.id;
              if (typeof pid === 'number') {
                const savedById = bucket.byPersonId.get(pid);
                if (savedById) {
                  ip.person.documentExpiresAt = savedById;
                  return;
                }
              }

              if (ip?.isPrimary && bucket.primary) {
                ip.person.documentExpiresAt = bucket.primary;
                return;
              }

              const rel = normalizeRelation(
                ip?.person?.primaryInsuredRelation ?? ip?.person?.relationship,
              );
              if (rel === 'spouse' && bucket.spouse) {
                ip.person.documentExpiresAt = bucket.spouse;
                return;
              }
              if (rel === 'child') {
                const next = bucket.children[childIdx++];
                if (next) {
                  ip.person.documentExpiresAt = next;
                }
              }
              if (rel === 'other' && bucket.others?.length) {
                const next = bucket.others.shift();
                if (next) {
                  ip.person.documentExpiresAt = next;
                }
              }
            });
          });
        }
        // Re-inject birthDate when backend omits it.
        if (birthByDetail.size && enriched?.details) {
          enriched.details.forEach((d: any) => {
            if (d?.id == null) return;
            const bucket = birthByDetail.get(d.id);
            if (!bucket) return;

            let childIdx = 0;
            (d.insuredPeople || []).forEach((ip: any) => {
              if (!ip?.person) return;
              if (ip.person.birthDate) return;

              const pid = ip?.personId ?? ip?.person?.id;
              if (typeof pid === 'number') {
                const savedById = bucket.byPersonId.get(pid);
                if (savedById) {
                  ip.person.birthDate = savedById;
                  return;
                }
              }

              if (ip?.isPrimary && bucket.primary) {
                ip.person.birthDate = bucket.primary;
                return;
              }

              const rel = normalizeRelation(
                ip?.person?.primaryInsuredRelation ?? ip?.person?.relationship,
              );
              if (rel === 'spouse' && bucket.spouse) {
                ip.person.birthDate = bucket.spouse;
                return;
              }
              if (rel === 'child') {
                const next = bucket.children[childIdx++];
                if (next) {
                  ip.person.birthDate = next;
                }
              }
              if (rel === 'other' && bucket.others?.length) {
                const next = bucket.others.shift();
                if (next) {
                  ip.person.birthDate = next;
                }
              }
            });
          });
        }
        // Re-inject isPep exactly as saved (including null) when backend coerces it.
        if (isPepByDetail.size && enriched?.details) {
          enriched.details.forEach((d: any) => {
            if (d?.id == null) return;
            const bucket = isPepByDetail.get(d.id);
            if (!bucket) return;

            let childIdx = 0;
            (d.insuredPeople || []).forEach((ip: any) => {
              if (!ip?.person) return;
              let saved: boolean | null | undefined;

              const pid = ip?.personId ?? ip?.person?.id;
              if (typeof pid === 'number' && bucket.byPersonId.has(pid)) {
                saved = bucket.byPersonId.get(pid);
              } else if (ip?.isPrimary) {
                saved = bucket.primary;
              } else {
                const rel = normalizeRelation(
                  ip?.person?.primaryInsuredRelation ?? ip?.person?.relationship,
                );
                if (rel === 'spouse') saved = bucket.spouse;
                else if (rel === 'child') saved = bucket.children[childIdx++];
                else if (rel === 'other' && bucket.others?.length) saved = bucket.others.shift();
              }

              if (saved === true || saved === false || saved === null) {
                ip.person.isPep = saved;
              }
            });
          });
        }
        // Re-inject primary insured contact/address if backend omitted them.
        if (primaryByDetail.size && enriched?.details) {
          enriched.details.forEach((d) => {
            if (d.id == null) return;
            const preserved = primaryByDetail.get(d.id);
            if (!preserved) return;
            const primary = (d.insuredPeople || []).find((ip) => ip.isPrimary);
            if (!primary) return;
            const person = (primary.person ||= {});
            const currentAddress = (person as any).address;
            const hasStreet = !!currentAddress?.street;
            const hasTownCode = !!currentAddress?.townCode;
            if ((!hasStreet || !hasTownCode) && preserved.address) {
              (person as any).address = {
                ...(currentAddress || {}),
                ...preserved.address,
              };
            }
            if (!primary.contactEmail && preserved.contactEmail != null) {
              primary.contactEmail = preserved.contactEmail;
            }
            if (!primary.homePhone && preserved.homePhone != null) {
              primary.homePhone = preserved.homePhone;
            }
            if (!primary.mobilePhone && preserved.mobilePhone != null) {
              primary.mobilePhone = preserved.mobilePhone;
            }
          });
        }
        this.setCurrentQuote(enriched);
      }),
    );
  }

  /** Retrieve a Quote by ID, optionally filtered by email */
  getQuote(quoteId: number, email?: string): Observable<QuoteResponseDto> {
    const params = email ? { email } : undefined;
    return this.get<QuoteResponseDto>(params, String(quoteId)).pipe(
      switchMap((resp) => this.enrichWithInsuranceTypes(resp)),
      tap((enriched) => this.setCurrentQuote(enriched)),
    );
  }

  /** Retrieve only the summary for a Quote by ID (direct endpoint) */
  getQuoteSummary(
    quoteId: number,
    payments?: number,
  ): Observable<import('../dtos/quote-summary.dto').QuoteSummaryDto | null> {
    const params =
      typeof payments === 'number' ? { payments: String(payments) } : {};
    return this.get<import('../dtos/quote-summary.dto').QuoteSummaryDto>(
      params,
      `${quoteId}/summary`,
    ).pipe(switchMap((resp) => of(resp ?? null)));
  }

  /** Persist current in-memory quote to backend via PUT */
  saveCurrentQuote(): Observable<QuoteResponseDto> {
    const q = this.currentQuote;
    if (!q?.quoteId) {
      throw new Error('No hay una cotización activa para guardar.');
    }

    // User attribution is auto-filled in updateQuote via applyUserAttribution
    const body: UpdateQuoteRequestDto = {
      quoteEmail: q.quoteEmail ?? null,
      contactEmail: q.contactEmail ?? null,
      producerCode: denormalizeProducerCode(q.producerCode),
      retailer: q.retailer ?? null,
      supervisor: q.supervisor ?? null,
      manager: q.manager ?? null,
      branch: q.branch ?? null,
      details: (q.details ?? []).map((d) => ({
        id: d.id ?? null,
        typeId: d.typeId,
        selectedPlanId: d.selectedPlanId ?? null,
        durationYears: d.durationYears ?? null,
        currency: d.currency ?? null,
        coverages: (d.coverages ?? []).map((c) => ({
          id: c.id ?? null,
          coverageId: c.coverage?.id ?? c.coverageId ?? null,
          coverageCode: c.coverage?.coverageCode ?? c.coverageCode ?? null,
          name: c.name ?? null,
          description: c.description ?? null,
          amount: c.amount ?? 0,
          currency: d.currency ?? null,
        })),
        insuredPeople: (d.insuredPeople ?? []).map((p) => ({
          id: p.id ?? null,
          personId: p.personId ?? null,
          isPrimary: p.isPrimary ?? false,
          // Include contact fields at insured level per Swagger
          contactEmail: p.contactEmail ?? null,
          homePhone: p.homePhone ?? null,
          mobilePhone: p.mobilePhone ?? null,
          person: p.person
            ? {
              id: p.person.id ?? null,
              name: p.person.name ?? '',
              lastName: p.person.lastName ?? '',
              documentType: p.person.documentType ?? '',
              documentNumber: p.person.documentNumber ?? '',
              documentExpiresAt: toIsoDate(p.person.documentExpiresAt) ?? null,
              birthDate: toIsoDate(p.person.birthDate) ?? null,
              gender: (p.person.gender as Gender) ?? null,
              civilState: p.person.civilState ?? '',
              heightCm: p.person.heightCm ?? null,
              weightLb: p.person.weightLb ?? null,
              nacionality: p.person.nacionality ?? '',
              personAddressId: p.person.personAddressId ?? null,
              isPep: p.person.isPep ?? null,
              occupation: p.person.occupation ?? null,
              relationship: p.person.relationship ?? null,
              primaryInsuredRelation: p.person.primaryInsuredRelation ?? null,
              // Forward nested address if present
              address: this.normalizeAddressForRequest(p.person.address ?? null),
            }
            : undefined,
        })),
        answers: (d.answers ?? []).map((a) => ({
          id: a.id ?? null,
          questionId: a.questionId!,
          value: a.value ?? null,
        })),
      })),
    };
    return this.updateQuote(q.quoteId, body as any);
  }

  /** Update local session copy: remove a detail by id */
  deleteDetailLocal(detailId: number): void {
    const q = this.currentQuote;
    if (!q) return;
    const details = (q.details ?? []).filter((d) => d.id !== detailId);
    this.setCurrentQuoteLocal({ ...q, details });
  }

  /** Update local session copy: set coverages for a detail */
  setDetailCoveragesLocal(
    detailId: number,
    coverages: NonNullable<QuoteResponseDto['details']>[number]['coverages'],
  ): void {
    const q = this.currentQuote;
    if (!q) return;
    const details = (q.details ?? []).map((d) =>
      d.id === detailId ? { ...d, coverages } : d,
    );
    this.setCurrentQuoteLocal({ ...q, details });
  }

  /** Perform payment for a Quote */
  payQuote(
    quoteId: number,
    body: QuotePaymentRequestDto,
  ): Observable<QuotePaymentResponseDto> {
    return this.create<QuotePaymentRequestDto, QuotePaymentResponseDto>(
      body,
      `${quoteId}/payment`,
    );
  }

  /** Create a payment session for a Quote
   * Currency is determined from the first detail's currency as per spec.
   */
  createPaymentSession(
    quoteId: number,
    amount: number,
  ): Observable<PaymentSessionResponseDto> {
    const currencyFromFirstDetail =
      this.currentQuote?.details?.[0]?.currency || 'DOP';
    const currencyCode = String(currencyFromFirstDetail).toUpperCase();

    const body: PaymentSessionRequestDto = {
      currencyCode,
      amount,
    };

    return this.create<PaymentSessionRequestDto, PaymentSessionResponseDto>(
      body,
      `${quoteId}/payment/sessions`,
    );
  }

  /** Issue a Quote */
  issueQuote(
    quoteId: number,
    body: IssueQuoteRequestDto,
  ): Observable<IssueQuoteResponseDto> {
    return this.create<IssueQuoteRequestDto, IssueQuoteResponseDto>(
      body,
      `${quoteId}/issue`,
    ).pipe(
      switchMap((response) => {
        if (response.quote) {
          return this.enrichWithInsuranceTypes(response.quote).pipe(
            tap((enriched) => {
              enriched.transactionId = response.transactionId;
              enriched.authorizationCode = response.authorizationCode;
              // Preserve existing summary since backend doesn't return it
              enriched.summary = this.currentQuote?.summary ?? enriched.summary;
              this.setCurrentQuote(enriched);
            }),
            switchMap(() => of(response)),
          );
        }
        return of(response);
      }),
    );
  }

  /** Validate whether an insured person already has an active policy for the quote's products */
  validateActivePolicy(
    quoteId: number,
    documentNumber: string,
  ): Observable<{ hasActivePolicy: boolean; message?: string }> {
    return this.get<{ hasActivePolicy: boolean; message?: string }>(
      { documentNumber },
      `${quoteId}/validate-active-policy`,
    );
  }

  downloadQuotePdf(quoteId: number): Observable<Blob> {
    const url = `${this.controller}/${quoteId}/pdf`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  /**
   * Ensures that each quote detail has its InsuranceType loaded.
   * Used both when fetching a quote and when hydrating from session.
   */
  private enrichWithInsuranceTypes(
    resp: QuoteResponseDto,
  ): Observable<QuoteResponseDto> {
    const details = resp?.details ?? [];
    const typeIds = Array.from(
      new Set(
        details
          .map((d) => d.typeId)
          .filter((id): id is number => typeof id === 'number'),
      ),
    );

    if (typeIds.length === 0) {
      // Nothing to enrich
      return of(resp);
    }

    return forkJoin(
      typeIds.map((id) => this.insuranceTypesService.getById(id)),
    ).pipe(
      switchMap((types) => {
        const byId = new Map<number, any>();
        types.forEach((t) => {
          if (typeof t.id === 'number') byId.set(t.id, t);
        });

        const enriched: QuoteResponseDto = {
          ...resp,
          details: details.map((d) => ({
            ...d,
            insuranceType:
              (typeof d.typeId === 'number' && byId.get(d.typeId)) ||
              d.insuranceType /* keep if backend still sends it */,
          })),
        };

        return of(enriched);
      }),
    );
  }
}
